import MapBoxMap from 'components/mapboxmap/MapBoxMap';
import PulsatingDot from 'components/pulsatingDot/PulsatingDot';
import { Courier, ZoneGeometryDto } from 'core/graphql/types';
import { TrafficUpdatesGrpcServiceClient } from 'core/grpc/TrafficUpdatesServiceClientPb';
import useAppSelector from 'core/hooks/useAppSelector';
import useLastCouriersPositionsDataStore from 'core/hooks/useLastCouriersPositionsDataStore';
import useUrl from 'core/hooks/useUrl';
import { formatDate, formatDateTime } from 'core/utils/DateUtils';
import { selectZoomLevel } from 'pages/itinerary/MapSlice';
import { useCallback, useMemo, useState } from 'react';
import { Layer, MapLayerMouseEvent, Marker, Popup, Source } from 'react-map-gl';
import { transformZonesToFeatureCollection } from '../../core/utils/GeoJsonUtils';

interface PopupInfo {
  courierIndicative: string;
  latitude: number;
  longitude: number;
  timestamp: string;
}

const trafficUpdatesServiceUrl = `${process.env.REACT_APP_HISTORICAL_DATA_GRPC_URL}`;
const client = new TrafficUpdatesGrpcServiceClient(trafficUpdatesServiceUrl);

export interface AgencyMapViewProps {
  couriers: Courier[];
  agencyZones: ZoneGeometryDto[];
}

const AgencyMapView = ({ couriers, agencyZones }: AgencyMapViewProps) => {
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
  const { paramValue: dateParamValue } = useUrl<string>('Date');
  const { paramValue: agencyParamValue } = useUrl<string>('Agency');
  const mapZoomLevel = useAppSelector(selectZoomLevel);
  const { couriersLastPositions } = useLastCouriersPositionsDataStore(
    couriers,
    client,
  );

  const [zonePopupInfo, setZonePopupInfo] = useState<{
    lng: number;
    lat: number;
    properties: GeoJSON.GeoJsonProperties | null;
  } | null>(null);

  const onMapMouseMove = useCallback((event: MapLayerMouseEvent) => {
    const { features, lngLat } = event;
    const hoveredFeature =
      features && features.find((f) => f.layer.id === 'zone-layer');

    if (hoveredFeature) {
      setZonePopupInfo({
        lng: lngLat.lng,
        lat: lngLat.lat,
        properties: hoveredFeature.properties,
      });
    } else {
      setZonePopupInfo(null);
    }
  }, []);

  const dateMatchesSelectedDate = (inputDateTime: string) => {
    const dayOfInputString = new Date(inputDateTime).toISOString().slice(0, 10);
    return dayOfInputString === dateParamValue;
  };

  const couriersPositionMarkers = useMemo(() => {
    const markerArray: any[] = [];
    couriersLastPositions.forEach((element) => {
      if (dateMatchesSelectedDate(element.getDateTime())) {
        const indicative = couriers.find(
          (x) => x.courierId === element.getCourierId(),
        )?.indicative!;
        markerArray.push(
          <Marker
            longitude={element.getLongitude()}
            latitude={element.getLatitude()}
            anchor="center"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              setPopupInfo({
                courierIndicative: indicative,
                latitude: element.getLatitude(),
                longitude: element.getLongitude(),
                timestamp: formatDateTime(new Date(element.getDateTime())),
              });
            }}
          >
            <PulsatingDot title={indicative} />
          </Marker>,
        );
      }
    });
    return markerArray;
  }, [couriersLastPositions, dateMatchesSelectedDate]);

  return (
    <div className="relative h-full">
      {dateParamValue !== formatDate(new Date()) && agencyParamValue && (
        <div className="absolute left-0 right-0 top-0 bottom-0 z-10 m-auto h-24 w-64 rounded-xl bg-light-gray p-2 text-center text-black">
          In order to view detailed courier route from previous days, please use
          Itinerary Details page
        </div>
      )}
      <div className="flex-grow-1" style={{ width: '100%', height: '100%' }}>
        <MapBoxMap
          id="agency-map"
          zoomLevel={mapZoomLevel}
          interactiveLayerIds={['zone-layer', 'zone-border-layer']}
          onMouseMove={onMapMouseMove}
        >
          <Source
            id="zones"
            type="geojson"
            data={transformZonesToFeatureCollection(agencyZones, [])}
          >
            <Layer
              id="zone-layer"
              type="fill"
              paint={{
                'fill-color': '#FF0000',
                'fill-opacity': 0.05,
              }}
            />
            <Layer
              id="zone-border-layer"
              type="line"
              paint={{
                'line-color': '#FF0000',
                'line-width': 0.1,
              }}
            />
          </Source>
          {couriersPositionMarkers}
          {popupInfo && (
            <Popup
              anchor="bottom"
              latitude={Number(popupInfo.latitude)}
              longitude={Number(popupInfo.longitude)}
              onClose={() => setPopupInfo(null)}
              className="text-black"
              offset={30}
            >
              <div className="mx-2">
                <div className="font-bold">
                  {`Courier: ${popupInfo.courierIndicative}`}
                </div>
                <div>{`Timestamp: ${popupInfo.timestamp}`}</div>
              </div>
            </Popup>
          )}

          {zonePopupInfo && (
            <Popup
              longitude={zonePopupInfo.lng}
              latitude={zonePopupInfo.lat}
              anchor="top"
              closeButton={false}
              onClose={() => setPopupInfo(null)}
            >
              <div>
                <h3 className="text-black">
                  {zonePopupInfo.properties?.zoneCode}
                </h3>
              </div>
            </Popup>
          )}
        </MapBoxMap>
      </div>
    </div>
  );
};

export default AgencyMapView;
